import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Serializers
// ---------------------------------------------------------------------------

type Serializer<T> = {
  serialize: (value: T) => string | null; // null = omit from URL
  parse: (raw: string | null) => T | undefined; // undefined = use default
};

const STRING_SER: Serializer<string> = {
  serialize: (v) => (v ? v : null),
  parse: (v) => (v == null ? undefined : v),
};

const BOOLEAN_SER: Serializer<boolean> = {
  serialize: (v) => (v ? '1' : null),
  parse: (v) => (v == null ? undefined : v === '1'),
};

const NUMBER_SER: Serializer<number> = {
  serialize: (v) => String(v),
  parse: (v) => {
    if (v == null) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  },
};

const STRING_ARRAY_SER: Serializer<string[]> = {
  serialize: (v) => (v.length ? v.join(',') : null),
  parse: (v) => (v == null ? undefined : v === '' ? [] : v.split(',')),
};

const SERIALIZERS: Record<string, Serializer<any>> = {
  string: STRING_SER,
  boolean: BOOLEAN_SER,
  number: NUMBER_SER,
  'string[]': STRING_ARRAY_SER,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ParamType = 'string' | 'boolean' | 'number' | 'string[]';

export interface ParamDef<T = any> {
  /** URL param type. Defaults to 'string'. */
  type?: ParamType;
  /** Default value when URL param is absent. */
  default: T;
  /** URL param name override. Defaults to the schema key. */
  urlKey?: string;
}

export type FilterSchema = Record<string, ParamDef>;

type InferValue<D extends ParamDef> =
  D extends ParamDef<infer T> ? T : never;

type SchemaValues<S extends FilterSchema> = {
  [K in keyof S]: InferValue<S[K]>;
};

export interface FilterParamsResult<S extends FilterSchema> {
  /** Current resolved values (URL param > default). */
  values: SchemaValues<S>;
  /** Set a single filter param. */
  set: <K extends keyof S & string>(key: K, value: SchemaValues<S>[K]) => void;
  /** Set multiple filter params at once. */
  setMany: (updates: Partial<SchemaValues<S>>) => void;
  /** Reset all filter params to defaults (clears them from URL). */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages a batch of URL search params for list-page filters.
 *
 * - Reads from URL params with fallback to schema defaults
 * - Writes via `setSearchParams` with `replace: true` (no history pollution)
 * - Preserves URL params NOT in the schema (e.g. `?modal=...`)
 * - Omits params from URL when they equal the default value
 */
export function useFilterParams<S extends FilterSchema>(
  schema: S,
): FilterParamsResult<S> {
  const [searchParams, setSearchParams] = useSearchParams();
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  // React Router hands a functional update the params of the render it came from, so several
  // writes in one handler (a Reset, a sort key and its direction) would each start from the
  // same params and only the last would land. Each write builds on this instead; every render
  // brings it back to the URL.
  const latestParamsRef = useRef(searchParams);
  latestParamsRef.current = searchParams;

  // Resolve current values from URL params + defaults
  const values = useMemo(() => {
    const result: any = {};
    for (const [key, def] of Object.entries(schema)) {
      const urlKey = def.urlKey ?? key;
      const ser = SERIALIZERS[def.type ?? 'string'] ?? STRING_SER;
      const raw = searchParams.get(urlKey);
      const parsed = ser.parse(raw);
      result[key] = parsed !== undefined ? parsed : def.default;
    }
    return result as SchemaValues<S>;
  }, [searchParams, schema]);

  const applyUpdates = useCallback(
    (updates: Partial<SchemaValues<S>>) => {
      const next = new URLSearchParams(latestParamsRef.current);
      const s = schemaRef.current;
      for (const [key, value] of Object.entries(updates)) {
        const def = s[key];
        if (!def) continue;
        const urlKey = def.urlKey ?? key;
        const ser = SERIALIZERS[def.type ?? 'string'] ?? STRING_SER;
        const serialized = ser.serialize(value);
        // Omit from URL if value equals default. An empty value whose default is not empty (a saved
        // preference used as the default) is kept as an empty param, or it would read as the default.
        const defaultSerialized = ser.serialize(def.default);
        if (serialized === defaultSerialized) {
          next.delete(urlKey);
        } else {
          next.set(urlKey, serialized ?? '');
        }
      }
      latestParamsRef.current = next;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const set = useCallback(
    <K extends keyof S & string>(key: K, value: SchemaValues<S>[K]) => {
      applyUpdates({ [key]: value } as unknown as Partial<SchemaValues<S>>);
    },
    [applyUpdates],
  );

  const reset = useCallback(() => {
    const next = new URLSearchParams(latestParamsRef.current);
    const s = schemaRef.current;
    for (const [key, def] of Object.entries(s)) {
      next.delete(def.urlKey ?? key);
    }
    latestParamsRef.current = next;
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  return { values, set, setMany: applyUpdates, reset };
}
